import type {
  Dispatch,
  KeyboardEventHandler,
  MutableRefObject,
  ReactElement,
  ReactNode,
} from 'react'
import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useLocale } from '../contexts/LocaleContext'
import { useInitialRender } from '../hooks/useInitialRender'
import usePrevious from '../hooks/usePrevious'
import type SvgIcon from '../SvgIcon/SvgIcon'
import { Directions } from '../utilities/directions'
import { Keys } from '../utilities/keyboard'
import type { Actions } from './Actions'
import { ActionTypes } from './Actions'
import Body from './Body'
import BulkActions from './BulkActions'
import GlobalActions from './GlobalActions'
import Head from './Head'
import Header from './Header'
import Pagination from './Pagination'
import Search from './Search'
import Title from './Title'
import Toolbar from './Toolbar'

export interface GroupedTableColumns {
  selected: TableColumn[]
  unselected: TableColumn[]
}

export type Limit = 25 | 50 | 100

export type FilterOptions = {
  columnKey: string
  value: string
  checked: boolean
}

export type SortOptions = {
  direction: Directions.Ascending | Directions.Descending
  key: keyof TableRow
}

export type ClickRowActionEventHandler = (row: TableRow) => void

type FilterEventHandler = ({ columnKey, value, checked }: FilterOptions) => void

type SelectEventHandler = (rows: TableRow[]) => void

type SortEventHandler = ({ direction, key }: SortOptions) => void

export interface TableColumn {
  filter?: { label: string; value: string; checked: boolean }[]
  key: string
  selected: boolean
  sort?: Directions.Ascending | Directions.Descending
  sortable?: boolean
  value: ReactNode
}

type TableReducers = {
  [ActionType in ActionTypes]: (
    state: TableState,
    actions: Actions,
  ) => TableState
}

interface TableProps {
  children?: ReactElement | ReactElement[]
  columns: TableColumn[]
  dense?: boolean
  isScrolling?: boolean
  limit?: Limit
  loading?: boolean
  onFilterChange?: FilterEventHandler
  onSort?: SortEventHandler
  onSelect?: SelectEventHandler
  rowActions?: {
    enabled?: (row: TableRow) => boolean
    label: ReactNode
    handler: ClickRowActionEventHandler
    startAdornment?: typeof SvgIcon
  }[]
  rows: TableRow[]
  selectable?: boolean
  selectionsRef?: MutableRefObject<string[]>
  storageKey?: string
  total?: number
}

export interface TableRow {
  // TODO: Figure out if there is a way to make the TableRow `key` correspond to TableColumn `key`
  [key: string]: string | ReactNode
  id: string
  selected?: boolean
}

interface TableState {
  columns: GroupedTableColumns
  dense: boolean
  isScrolling?: boolean
  limit: Limit
  loading: boolean
  onFilterChange?: FilterEventHandler
  onSelect?: SelectEventHandler
  onSort?: SortEventHandler
  page: number
  rowActions?: {
    enabled?: (row: TableRow) => boolean
    label: ReactNode
    handler: ClickRowActionEventHandler
    startAdornment?: typeof SvgIcon
  }[]
  rows: TableRow[]
  selectionsRef?: MutableRefObject<string[]>
  selectable: boolean
  total: number
}

type UseTableValues = [TableState, Dispatch<Actions>]

const partition = (columns: TableColumn[]) =>
  columns.reduce(
    (previous, current) => {
      if (current.selected) {
        previous.selected.push(current)
      } else {
        previous.unselected.push(current)
      }

      return previous
    },
    { selected: [], unselected: [] } as GroupedTableColumns,
  )

const populate = (
  defaultColumns: TableColumn[],
  initialRender: boolean,
  storageKey?: string,
): TableColumn[] => {
  if (storageKey && initialRender) {
    const serializedColumns = window.localStorage.getItem(storageKey)

    if (serializedColumns) {
      const storedColumns = JSON.parse(serializedColumns) as TableColumn[]
      const newColumns = defaultColumns.filter(
        (defaultColumn) =>
          !storedColumns.some(
            (storedColumn) => defaultColumn.key === storedColumn.key,
          ),
      )

      return storedColumns
        .filter((storedColumn) =>
          defaultColumns.find(
            (defaultColumn) => defaultColumn.key === storedColumn.key,
          ),
        )
        .map((storedColumn) => {
          const defaultColumn = defaultColumns.find(
            (defaultColumn) => defaultColumn.key === storedColumn.key,
          )
          const column = {
            ...storedColumn,
            value: defaultColumn?.value || storedColumn.value,
          }

          if (defaultColumn?.filter) {
            column.filter = [...defaultColumn.filter]
          }

          return column
        })
        .concat(newColumns)
    }
  }

  return defaultColumns
}

const reducers: TableReducers = {
  [ActionTypes.ChangeLimit]: (
    state,
    action: Extract<Actions, { type: ActionTypes.ChangeLimit }>,
  ) => {
    const start = state.rows.length > 0 ? (state.page - 1) * state.limit + 1 : 0
    const end =
      state.rows.length > 0
        ? state.rows.length < action.limit
          ? start + state.rows.length
          : start + action.limit - 1
        : 0

    let page = end < action.limit ? 1 : state.page

    // Recalculate the page if the rows end before the current page
    if (state.total < (state.page - 1) * action.limit + 1) {
      page = Math.ceil(state.total / action.limit)
    }

    return {
      ...state,
      limit: action.limit,
      page: page > 0 ? page : 1,
    }
  },
  [ActionTypes.FilterChange]: (
    state,
    action: Extract<Actions, { type: ActionTypes.FilterChange }>,
  ) => {
    const columns = [...state.columns.selected, ...state.columns.unselected]

    return {
      ...state,
      columns: partition(
        columns.map((column) => {
          if (column.key === action.key) {
            column.filter?.map((filter) => {
              if (filter.value === action.value) {
                filter.checked = action.checked

                state.onFilterChange?.({
                  columnKey: action.key,
                  value: action.value,
                  checked: action.checked,
                })
              }

              return filter
            })
          }

          return column
        }),
      ),
    }
  },
  [ActionTypes.FirstPage]: (state) => ({ ...state, page: 1 }),
  [ActionTypes.MoveColumn]: (
    state,
    action: Extract<Actions, { type: ActionTypes.MoveColumn }>,
  ) => {
    let columns = state.columns.selected

    if (state.selectable) {
      columns = columns.slice(1)
    }

    const oldIndex = columns.findIndex((column) => column.key === action.key)
    const newIndex =
      action.direction === Directions.Left ? oldIndex - 1 : oldIndex + 1

    columns.splice(newIndex, 0, columns.splice(oldIndex, 1)[0])

    return {
      ...state,
      columns: partition([
        ...(state.selectable ? [state.columns.selected[0]] : []),
        ...columns,
        ...state.columns.unselected,
      ]),
    }
  },
  [ActionTypes.NextPage]: (state) => ({ ...state, page: state.page + 1 }),
  [ActionTypes.PreviousPage]: (state) => ({ ...state, page: state.page - 1 }),
  [ActionTypes.SortColumn]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SortColumn }>,
  ) => {
    const columns = [...state.columns.selected, ...state.columns.unselected]

    return {
      ...state,
      columns: partition(
        columns.map((column) => {
          if (column.key === action.key) {
            column.sort = action.direction

            state.onSort?.({
              direction: column.sort,
              key: action.key,
            })
          } else {
            column.sort = undefined
          }

          return column
        }),
      ),
      page: 1,
    }
  },
  [ActionTypes.ToggleColumn]: (
    state,
    action: Extract<Actions, { type: ActionTypes.ToggleColumn }>,
  ) => {
    const columns = [...state.columns.selected, ...state.columns.unselected]

    state.columns.selected.forEach((column, index, columns) => {
      if (column.key === action.key) {
        const focusable = columns[index + 1] || columns[index - 1]
        const element = document.querySelector(
          `th[data-key="${focusable.key}"]`,
        ) as HTMLTableHeaderCellElement

        element?.focus()
        element?.setAttribute('tabIndex', '0')
      }
    })

    return {
      ...state,
      columns: partition(
        columns.map((column) => {
          if (column.key === action.key) {
            column.selected = !column.selected
          }

          return column
        }),
      ),
    }
  },
  [ActionTypes.ToggleRow]: (
    state,
    action: Extract<Actions, { type: ActionTypes.ToggleRow }>,
  ) => {
    const rows = state.rows.map((row) => {
      if (row.id === action.key) {
        row.selected = !row.selected

        if (action.selectionsRef === undefined) {
          return row
        }

        if (row.selected) {
          action.selectionsRef.current.push(row.id)
        } else {
          const deselectedRow = action.selectionsRef.current.indexOf(row.id)
          action.selectionsRef.current.splice(deselectedRow, 1)
        }
      }

      return row
    })

    if (state.onSelect) {
      state.onSelect(state.rows.filter((row) => row.selected))
    }

    return {
      ...state,
      rows,
    }
  },
  [ActionTypes.ToggleRows]: (
    state,
    action: Extract<Actions, { type: ActionTypes.ToggleRows }>,
  ) => {
    const rows = state.rows.map((row) => {
      row.selected = action.status === 'checked'

      if (action.selectionsRef === undefined) {
        return row
      }

      if (row.selected && action.selectionsRef.current.indexOf(row.id) === -1) {
        action.selectionsRef.current.push(row.id)
      }

      if (!row.selected) {
        const deselectedRow = action.selectionsRef.current.indexOf(row.id)
        action.selectionsRef.current.splice(deselectedRow, 1)
      }

      return row
    })

    if (state.onSelect) {
      state.onSelect(state.rows.filter((row) => row.selected))
    }

    return {
      ...state,
      rows,
    }
  },
  [ActionTypes.UpdateRows]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UpdateRows }>,
  ) => {
    const selectedRows = state.rows.filter((row) => row.selected)

    if (selectedRows.length > 0) {
      for (const selectedRow of selectedRows) {
        const index = action.rows.findIndex((row) => row.id === selectedRow.id)

        if (index > -1) {
          action.rows[index].selected = true
        }
      }
    }

    if (action.selectionsRef) {
      action.selectionsRef.current = action.selectionsRef.current.filter(
        (selectedRow) =>
          action.rows.find((row) => row.id === selectedRow) !== undefined,
      )
    }

    let page = state.page

    // Recalculate the page if the new rows end before the current page
    if (action.total < (state.page - 1) * state.limit + 1) {
      page = Math.ceil(action.total / state.limit)
    }

    return {
      ...state,
      page: page > 0 ? page : 1,
      rows: action.rows,
      total: action.total,
    }
  },
  [ActionTypes.UpdateRowActions]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UpdateRowActions }>,
  ) => {
    return {
      ...state,
      rowActions: action.rowActions,
    }
  },
  [ActionTypes.UpdateLoading]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UpdateLoading }>,
  ) => {
    return {
      ...state,
      loading: action.loading,
    }
  },
  [ActionTypes.UpdateColumns]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UpdateColumns }>,
  ) => {
    return {
      ...state,
      columns: action.columns,
    }
  },
  [ActionTypes.UpdateScrolling]: function (
    state,
    actions: Extract<Actions, { type: ActionTypes.UpdateScrolling }>,
  ): TableState {
    return {
      ...state,
      isScrolling: actions.isScrolling,
    }
  },
}

const runReducer = (state: TableState, action: Actions): TableState =>
  reducers[action.type](state, action)

const TableContext = createContext<UseTableValues | null>(null)

export const useTable = (): UseTableValues =>
  useContext(TableContext) as UseTableValues

function Table({
  children,
  columns: initialColumns,
  dense = false,
  isScrolling = false,
  limit = 50,
  loading = false,
  onFilterChange,
  onSelect,
  onSort,
  rowActions,
  rows,
  selectable = false,
  selectionsRef,
  storageKey,
  total = rows.length,
}: TableProps): JSX.Element {
  const initialRender = useInitialRender()
  const header = useRef<HTMLElement | null>(null)
  const tableContainer = useRef<HTMLDivElement | null>(null)
  const [headerBottomPosition, setHeaderBottomPosition] = useState(0)
  const [locale] = useLocale()
  const previousLocale = usePrevious(locale)
  const [state, dispatch] = useReducer(runReducer, {
    columns: partition(populate(initialColumns, initialRender, storageKey)),
    dense,
    limit,
    loading,
    onFilterChange,
    onSelect,
    onSort,
    page: 1,
    rowActions,
    rows,
    isScrolling,
    selectable,
    selectionsRef,
    total,
  } as TableState)

  const handleScroll = () => {
    if (!tableContainer.current) {
      return
    }

    dispatch({
      type: ActionTypes.UpdateScrolling,
      isScrolling: !(tableContainer.current.scrollTop === 0),
    })
  }

  const getHeaderBottomPosition = () => {
    if (header.current) {
      setHeaderBottomPosition(header.current.getBoundingClientRect().bottom)
    }
  }

  useEffect(() => {
    const container = tableContainer.current
    getHeaderBottomPosition()

    container?.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', getHeaderBottomPosition)

    return () => {
      container?.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', getHeaderBottomPosition)
    }
  }, [])

  useEffect(() => {
    if (locale === previousLocale) {
      return
    }

    if (initialColumns.some((column) => column.filter !== undefined)) {
      const columns = { ...state.columns }
      const keys = ['selected', 'unselected'] as const

      initialColumns.forEach((initialColumn) => {
        keys.forEach((key) => {
          const index = columns[key].findIndex(
            (column) => column.key === initialColumn.key && column.filter,
          )

          if (index > -1) {
            const column = columns[key][index]

            if (column.filter && initialColumn.filter) {
              column.filter = column.filter.map((filter) => ({
                ...filter,
                label:
                  initialColumn.filter?.find(
                    (initialFilter) => filter.value === initialFilter.value,
                  )?.label ?? filter.label,
              }))

              columns[key][index] = column
            }
          }
        })
      })

      dispatch({
        type: ActionTypes.UpdateColumns,
        columns,
      })

      if (selectable) {
        const index = columns.selected.findIndex(
          (column) => column.key === 'checkbox',
        )

        columns.selected = [
          ...columns.selected.slice(0, index),
          ...columns.selected.slice(index + 1),
        ]
      }

      if (storageKey) {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify([...columns.selected, ...columns.unselected]),
        )
      }
    }
  }, [
    initialColumns,
    locale,
    previousLocale,
    selectable,
    state.columns,
    storageKey,
  ])

  useEffect(() => {
    if (storageKey) {
      const columns = { ...state.columns }

      if (selectable) {
        const index = columns.selected.findIndex(
          (column) => column.key === 'checkbox',
        )

        columns.selected = [
          ...columns.selected.slice(0, index),
          ...columns.selected.slice(index + 1),
        ]
      }

      window.localStorage.setItem(
        storageKey,
        JSON.stringify([...columns.selected, ...columns.unselected]),
      )
    }
  }, [selectable, state.columns, storageKey])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateRows,
      rows,
      selectionsRef: state.selectionsRef,
      total,
    })
  }, [rows, state.selectionsRef, total])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateRowActions,
      rowActions,
    })
  }, [rowActions])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateLoading,
      loading,
    })
  }, [loading])

  const handleKeyDown: KeyboardEventHandler<HTMLElement> = (event) => {
    const cell = event.target as
      | HTMLTableCellElement
      | HTMLTableHeaderCellElement
    const row = cell.parentElement as HTMLTableRowElement
    const table = row.parentElement?.parentElement as HTMLTableElement

    const cellIndex = cell.cellIndex
    const rowIndex = row.rowIndex

    if (cellIndex === undefined || rowIndex === undefined) {
      return
    }

    let newCell

    switch (event.key) {
      case Keys.ArrowDown:
        newCell = table.rows[rowIndex + 1]?.cells[cellIndex]
        break
      case Keys.ArrowUp:
        newCell = table.rows[rowIndex - 1]?.cells[cellIndex]
        break
      case Keys.ArrowRight:
        newCell = table.rows[rowIndex].cells[cellIndex + 1]
        break
      case Keys.ArrowLeft:
        newCell = table.rows[rowIndex].cells[cellIndex - 1]
        break
    }

    if (newCell) {
      newCell.focus()
      newCell.setAttribute('tabIndex', '0')
      cell.setAttribute('tabIndex', '-1')
    }
  }

  const viewportHeight = window.innerHeight
  const headerHeight = header.current
    ? header.current.getBoundingClientRect().height
    : 0

  // Tables within the viewport have heights that rely on the bottom position of the header.
  // However, to give other tables (beyond the viewport) the same height, we need to rely on the height of the header instead.
  const isBelowViewport = headerBottomPosition > viewportHeight

  return (
    <TableContext.Provider value={[state, dispatch]}>
      <div>
        <header className="divide-y divide-black-10 bg-white-100" ref={header}>
          {children}
        </header>
        <section className="w-full max-w-full">
          <div
            className="overflow-auto"
            ref={tableContainer}
            style={{
              maxHeight: isBelowViewport
                ? `calc(${viewportHeight - headerHeight}px)`
                : `calc(${viewportHeight - headerBottomPosition}px)`,
            }}
          >
            <table
              cellPadding={0}
              className="w-full font-sans border-spacing-0 border-separate"
              onKeyDown={handleKeyDown}
              role="grid"
            >
              <Head />
              <Body />
            </table>
          </div>
        </section>
      </div>
    </TableContext.Provider>
  )
}

Table.BulkActions = BulkActions
Table.GlobalActions = GlobalActions
Table.Header = Header
Table.Pagination = Pagination
Table.Search = Search
Table.Title = Title

export default Table

Table.Toolbar = Toolbar
